import { useCallback } from 'react';
import { useMemo } from 'react';

import { useLocalizationContext } from '@order/Localization';
import { useTelemetry } from '@order/Telemetry';

import type {
  AccountSweetpassBillingHistorySection,
  AccountSweetpassCancellationSection,
  AccountSweetpassMembershipSection,
  AccountSweetpassPaymentMethodsSection,
} from '../useSweetpassAccountContentfulData';
import {
  ACCOUNT_SWEETPASS_BILLING_HISTORY_SECTION,
  ACCOUNT_SWEETPASS_CANCELLATION_SECTION,
  ACCOUNT_SWEETPASS_MEMBERSHIP_SECTION,
  ACCOUNT_SWEETPASS_PAYMENT_METHODS_SECTION,
  useSweetpassAccountContentfulData,
} from '../useSweetpassAccountContentfulData';
import { useSweetpassMembershipData } from '../useSweetpassMembershipData';
import {
  getBillingHistorySection,
  getCancellationSection,
  getMembershipLevelSection,
  getPaymentMethodsSection,
} from './utils';

export const useSweetpassMembershipContent = (
  props: UseSweetpassMembershipContentProps,
) => {
  const { track } = useTelemetry();
  const { formatMessage } = useLocalizationContext();
  const { content, isLoadingContentfulData } =
    useSweetpassAccountContentfulData();

  const {
    subscribeHandler,
    viewPaymentMethodHandler,
    addPaymentMethodHandler,
    viewBillingHistoryHandler,
  } = props;

  const {
    cancelSubscriptionAtCycleEnd,
    reactivateSubscription,
    ...sweetpassMembershipResult
  } = useSweetpassMembershipData();

  const cancelSubscriptionHandler = useCallback(async () => {
    // Once we add the confirmation modal, track sweetpass_account.cancel_confirmation_cta.tapped.
    track('sweetpass_account.cancel_cta.tapped');
    await cancelSubscriptionAtCycleEnd();
  }, [cancelSubscriptionAtCycleEnd, track]);

  const reactivateSubscriptionHandler = useCallback(async () => {
    // Is there a reactivate subscription confirmation modal?
    track('sweetpass_account.rejoin_cta.tapped');
    await reactivateSubscription();
  }, [reactivateSubscription, track]);

  return useMemo(() => {
    const { isSweetpassEnabled, isSweetpassMembershipLoading, ...membership } =
      sweetpassMembershipResult;

    const isSweetpassContentLoading =
      !isSweetpassEnabled ||
      isSweetpassMembershipLoading ||
      isLoadingContentfulData ||
      !membership.tier;

    if (isSweetpassContentLoading) {
      return {
        ...sweetpassMembershipResult,
        isSweetpassContentLoading,
        sweetpassMembershipContent: [],
      };
    }

    return {
      ...sweetpassMembershipResult,
      isSweetpassContentLoading,
      sweetpassMembershipContent: content?.map((section) => ({
        // ─── Membership Section ───────────────────────────────────

        ...(section.name === ACCOUNT_SWEETPASS_MEMBERSHIP_SECTION
          ? getMembershipLevelSection({
              ...(section as AccountSweetpassMembershipSection),
              ...membership,
              subscribeHandler,
              reactivateSubscriptionHandler,
              formatMessage,
            })
          : {}),

        // ─── Cancellation Section ─────────────────────────────────

        ...(section.name === ACCOUNT_SWEETPASS_CANCELLATION_SECTION
          ? getCancellationSection({
              ...(section as AccountSweetpassCancellationSection),
              ...membership,
              cancelSubscriptionHandler,
              formatMessage,
            })
          : {}),

        // ─── Payment Methods Section ──────────────────────────────

        ...(section.name === ACCOUNT_SWEETPASS_PAYMENT_METHODS_SECTION &&
        membership.hasPaidTierHistory
          ? getPaymentMethodsSection({
              ...(section as AccountSweetpassPaymentMethodsSection),
              ...membership,
              viewPaymentMethodHandler,
              addPaymentMethodHandler,
              formatMessage,
            })
          : {}),

        // ─── Billing History Section ──────────────────────────────

        ...(section.name === ACCOUNT_SWEETPASS_BILLING_HISTORY_SECTION
          ? getBillingHistorySection({
              ...(section as AccountSweetpassBillingHistorySection),
              ...membership,
              viewBillingHistoryHandler,
              formatMessage,
            })
          : {}),
      })),
    };
  }, [
    sweetpassMembershipResult,
    isLoadingContentfulData,
    content,
    subscribeHandler,
    reactivateSubscriptionHandler,
    cancelSubscriptionHandler,
    viewPaymentMethodHandler,
    addPaymentMethodHandler,
    viewBillingHistoryHandler,
    formatMessage,
  ]);
};

// ─── Types ───────────────────────────────────────────────────────────────────

type UseSweetpassMembershipContentProps = {
  readonly subscribeHandler: () => Promise<void>;
  readonly viewPaymentMethodHandler: (paymentMethodId: string) => void;
  readonly addPaymentMethodHandler: () => void;
  readonly viewBillingHistoryHandler: () => void;
};
